/*
 * File: ui-search.ts
 * File Created: 2023-12-25 03:49:13
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2026-01-23 10:12:50
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

import { AnyHelper, IMetaAttribute, ObjectHelper, StringHelper } from "@root/core";
import {
    BaseUITag,
    EUITagRuleType,
    EUITagRuleUnion,
    EUITagTarget,
    IUISearchSpecial,
    IUITagInput,
    IUITagResult,
    UITag,
} from "../core";
import { executeRuleFunction } from "../core/parse-rule-function";
import * as numeral from "numeral";
import { VueHolder } from "@root/core/utilities";

export type ISearchResult = IUISearchSpecial["search"];

export type ISearchHandleUnit = {
    equals?: any;
    lte?: any;
    gte?: any;
    contains?: any;
};

export type ISearchHandleElement = {
    [index: string]: ISearchHandleUnit | ISearchHandleElement;
};

export type ISearchHandle = {
    where: {
        OR: Array<ISearchHandleElement>;
    };
};

@UITag.set()
export class uiSearch extends BaseUITag {
    appliedTo: EUITagTarget[] = [
        EUITagTarget.TableSearch,
        // EUITagTarget.TableBodyCell,
        // EUITagTarget.TableBodyCellContainer,
    ];
    ruleTypes: EUITagRuleUnion[] = [EUITagRuleType.Object, EUITagRuleType.Custom];
    allowNullRule: boolean = true; /// default: true

    /// can be: rule | { [name]: [type, rule] | null (disable the search) }
    /// rule: true | false | equal | like | {enum object}
    /// type can be:
    // string: true | false | equal | like (default)
    // Date: true (default) | false
    // number: true (default) | false
    // boolean: true | false | equal (default)
    // enum: {enum object}
    // type any or else: true | false | equal (default)
    //////////////////////////////////////////////////////
    /// can also be object, should match IUISearchSpecial["search"]
    parse(input: IUITagInput): IUITagResult | null {
        const rule = this.parseRule(input);
        let search: IUISearchSpecial["search"] | undefined = undefined;
        if (rule == null || typeof rule !== "object") {
            const result = parseCustomRule(input, rule);

            const [name, value] = result;
            const [type, finalRule] = value ?? [];

            search = {
                [name]: value == null ? null : [type, finalRule],
            } as any;
        } else {
            search = rule as any;
        }

        return {
            specials: { search },
        };
    }

    /// match any value with `ISearchHandleUnit`
    /// return true (matched), false (not matched)
    static match(value: any, rule: ISearchHandleElement["string"]): boolean {
        let pass = false;
        /// should not be undefined
        value = value ?? null;

        thisRule: {
            for (const ruleKey of Object.keys(rule)) {
                const ruleValue = rule[ruleKey];

                switch (ruleKey) {
                    case "equals": {
                        if (value != ruleValue) break thisRule;
                        break;
                    }
                    case "lte": {
                        if (value > ruleValue) break thisRule;
                        break;
                    }
                    case "gte": {
                        if (value < ruleValue) break thisRule;
                        break;
                    }
                    case "contains": {
                        if (typeof value !== "string") break thisRule;
                        if (value.indexOf(ruleValue) < 0) break thisRule;
                        break;
                    }
                    default: {
                        if (uiSearch.match(value?.[ruleKey], ruleValue) === false)
                            break thisRule;
                        break;
                        //console.error(`<funcValueSearch> unknown rule key <${ruleKey}>`);
                        //break;
                    }
                }
            }
            pass = true;
        }
        return pass;
    }

    /// turn `ISearchResult` into `ISearchHandle`
    /// value is the searching string
    static handle(
        searches: ISearchResult,
        value: string | undefined,
    ): ISearchHandle | null {
        /// string: true | equal | like (default)
        /// Date: true (default)
        /// number: true (default)
        /// boolean: true | equal (default)
        /// type any or else: true | equal (default)

        if (value == undefined) return null;

        const defaultHandle = {
            where: { OR: [] },
        };
        /// firstly, trim the value
        value = value.trim();
        /// ignore empty value
        if (value === "") return null;

        return Object.keys(searches).reduce<ISearchHandle>((final, key) => {
            if (value == undefined) return final; /// should not happen

            const unit = searches[key];
            if (unit == null) return final;
            const [type, rule] = unit;
            /// debugger: get search param: key / type / rule here

            let resolver: ISearchHandle["where"]["OR"][number][string] | undefined =
                undefined;
            do {
                switch (type) {
                    case "string": {
                        if (rule == "equal") {
                            resolver = { equals: value };
                        } else {
                            resolver = { contains: value };
                        }
                        break;
                    }
                    case "boolean": {
                        const resolvedBoolean = boolResolver(value);
                        if (resolvedBoolean == null) break;
                        resolver = { equals: resolvedBoolean };
                        break;
                    }
                    case "number": {
                        const resolvedNumber = numberResolver(value);
                        if (resolvedNumber == null) break;
                        if (typeof resolvedNumber == "number") {
                            resolver = { equals: resolvedNumber };
                            break;
                        }
                        const [gte, lte] = resolvedNumber;
                        resolver = { gte, lte };
                        break;
                    }
                    case "Date": {
                        const resolvedDate = dateResolver(value);
                        if (resolvedDate == null) break;
                        if (resolvedDate instanceof Date) {
                            resolver = { equals: resolvedDate };
                            break;
                        }
                        const [gte, lte] = resolvedDate;
                        resolver = { gte, lte };
                        break;
                    }
                    case "enum": {
                        const resolvedEnum = enumResolver(value, rule);
                        if (resolvedEnum == null) break;
                        resolver = { equals: resolvedEnum };
                        break;
                    }
                    case "any": {
                        resolver = { equals: value };
                        break;
                    }
                }
            } while (0);

            /// if valid
            if (resolver != null) {
                /// recursive wrapping
                final.where.OR.push(ObjectHelper.wrap(key, resolver));
            }
            return final;
        }, defaultHandle);
    }
}

/// private helpers
// resolve boolean
function boolResolver(input: string): boolean | null {
    /// test the string strictly
    const value = input.trim();
    if (input == "true") return true;
    if (input == "false") return false;
    return null;
}

// resolve number
function numberResolver(input: string): number | [number, number] | null {
    const parts = input.split("~").map((v) => v.trim());

    /// not valid range
    if (parts.length > 2) return null;

    /// do resolve
    // single
    if (parts.length == 1) {
        const [part] = parts;
        const num1 = numeral(part).value();
        return num1;
    }

    /// range
    const [part1, part2] = parts;
    const num1 = numeral(part1).value();
    const num2 = numeral(part2).value();
    if (num1 == null || num2 == null) return null;
    return [num1, num2].sort((a, b) => a - b) as [number, number];
}

// resolve date
function dateResolver(input: string): [Date, Date] | null {
    const parts = input.split("~").map((v) => v.trim());

    /// not valid range
    if (parts.length > 2) return null;

    /// do resolve
    // single
    if (parts.length == 1) parts.push(parts[0]);

    // range
    const [part1, part2] = parts;
    const date1 = StringHelper.toDate(part1);
    const date2 = StringHelper.toDate(part2, true);
    if (date1 == null || date2 == null) return null;

    return [date1, date2];
}

// resolve enum
type EnumLike = Record<string, string | number>;
function enumResolver(input: string, rule: EnumLike): string | null {
    /// input 是任意字串
    /// rule 是要 match 的 enum
    /// 例如:
    // export enum EOrganizationType {
    //     "PERSONAL" = "w_OrganizationTypePersonal",
    //     "ORG" = "w_OrganizationTypeOrg",
    // }
    // 若是value 部份符合(like)此 enum 的任一 key，return 這個找到的 key
    // 他的 value 可以拿去翻譯，例如
    // VueHolder().$_("w_OrganizationTypePersonal")
    // 同樣的，翻譯出來的結果，若是與 value 部份符合(like)，return 這個找到的 key
    if (input == null) return null;
    const translate = VueHolder().$_;
    const norm = (s: unknown): string => String(s ?? "").trim();
    input = norm(input);

    // 準備候選：每個 enum 鍵名對應一組可比對的別名（鍵名 / 值 / 值的翻譯）
    type Candidate = { key: string; aliases: string[] };
    const candidates: Candidate[] = Object.keys(rule).map((k) => {
        const v = String(rule[k]);
        const aliases = new Set<string>();

        // 1) 原始鍵名
        aliases.add(norm(k));

        // 2) 值本身（字串或數字）
        aliases.add(norm(v));

        // 3) 翻譯後的顯示文字
        if (translate) {
            const t = translate(v);
            if (t != null) aliases.add(t);
        }
        return { key: k, aliases: [...aliases].filter(Boolean) };
    });

    // 先做「精準等值」比對
    for (const c of candidates) {
        if (c.aliases.some((a) => a === input)) return c.key;
    }

    // 再做「like 模糊比對」：
    // - input 包含 alias 或 alias 包含 input（且長度 >= minLikeLength）
    // - 若有多個命中，取分數最高者（越接近完整匹配越高）
    let best: { key: string; score: number } | null = null;

    const scoreLike = (a: string, b: string) => {
        // 越接近完全相等分數越高，其次是包含關係長度比
        if (a === b) return 1;
        if (a.length === 0 || b.length === 0) return 0;

        // a 包含 b
        if (a.includes(b)) return b.length / a.length;
        // b 包含 a
        if (b.includes(a)) return a.length / b.length;

        return 0;
    };

    for (const c of candidates) {
        for (const alias of c.aliases) {
            let s = 0;
            // 雙向包含都算分
            if (input.includes(alias) || alias.includes(input)) {
                s = Math.max(scoreLike(input, alias), scoreLike(alias, input));
            }

            if (s > 0 && (!best || s > best.score)) {
                best = { key: c.key, score: s };
            }
        }
    }

    return best?.key ?? null;
}

/// return value: [name, [type, finalResult]]
function parseCustomRule(
    input: IUITagInput,
    rule?: NonNullable<any> | null,
): [string, [IMetaAttribute["type"]["name"], any] | null] {
    rule = rule ?? input.rule ?? "true";

    const meta = input.meta;
    let { name, type } = meta.attr;
    let typename = type.name;
    let finalRule: any = undefined;
    const enumRule = meta.attr.tagMap?.uiEnum;
    const resolveEnumRule = () => {
        if (!enumRule) return null;
        const parsed = executeRuleFunction({ ...input, rule: enumRule } as IUITagInput);
        if (parsed && typeof parsed === "object") return parsed;
        return StringHelper.toObject(enumRule) ?? null;
    };

    do {
        /// false case
        if (AnyHelper.toBoolean(rule) == false) break;
        if (enumRule) {
            const enumMap = resolveEnumRule();
            if (enumMap) {
                typename = "enum";
                finalRule = enumMap;
                break;
            }
        }

        switch (typename) {
            /// string: true | equal | like (default) | (ENUM ARRAY, ex: ["ADMIN", "USER"])
            case "string":
                if (rule == "equal" || rule == "like") finalRule = rule;
                else if (rule == "true" || rule == true) finalRule = true;
                else {
                    /// Try parsing array

                    console.error(
                        `<uiSearch> rule for <${typename}> should be true | equal | like (default), got <${rule}>`,
                    );
                }
                break;
            /// Date: true (default)
            /// number: true (default)
            case "Date":
            case "number":
                if (rule == "true" || rule == true) finalRule = true;
                else {
                    console.error(
                        `<uiSearch> rule for <${typename}> should be true (default)`,
                    );
                }
                break;
            default:
                typename = "any";
            case "boolean":
                /// boolean: true | equal (default)
                /// type any or else: true | equal (default)
                if (rule == "equal") finalRule = rule;
                else if (rule == "true" || rule == true) finalRule = true;
                else {
                    console.error(
                        `<uiSearch> rule for <${typename}> should be true | equal (default)`,
                    );
                }
                break;
        }
    } while (0);

    /// false case
    if (finalRule == null) return [name, null];

    return [name, [typename, finalRule]];
}
